/**
 * resolveSelectionScope
 *
 * The single reading of "what is selected" every ArTable bulk consumer acts on:
 * the visible rows select-all governs, the ticks that fall inside them, and
 * whether the escalation to the whole table is still live. Used by
 * `ArHeaderCheckbox`, `ArSelectionScopeBar`, `ArBulkActionsMenu`,
 * `useRowDeleteTargets`, `useKeyboardNavigation` (⌘⌫) and `ArCell`'s bulk-edit
 * fan-out, so a row ticked before a split, column filter or visibility view hid
 * it can no longer be locked, duplicated, deleted or written to (SYD-2386).
 *
 * `isAllRows` is DERIVED, never stored: `'all'` was a statement about the set
 * that was fully ticked when the user made it, so widening the visible set —
 * clearing a filter or a split, changing the view, a row arriving on the page —
 * retires the escalation on the next read rather than waiting for a reset that
 * no site owns.
 *
 * The `AR_TABLE_PAGE_SIZE` slice is a backstop, not a filter: the page query
 * bounds the loaded rows at that many and a bundle's members come off the same
 * page, so it is a no-op in production. It lives here so the header checkbox and
 * every action mean the same thing by "the visible rows".
 *
 * Pure — no React, no Valtio, no I/O — so one function serves a snapshot at
 * render time and a bare proxy at event time.
 */

import { AR_TABLE_PAGE_SIZE } from '#types/System.Parameters.types';
import type { ArRowId } from '#types/System.Artifacts.types';

type ArTableSelectionScope = {
  /** The rows select-all governs on this page. */
  visibleRowIds: ArRowId[];
  /** Those of them that are ticked, in display order — what a 'visible' action touches. */
  selectedVisibleRowIds: ArRowId[];
  everyVisibleTicked: boolean;
  /** `'all'` is live only while every visible row is still ticked. */
  isAllRows: boolean;
};

function resolveSelectionScope(params: {
  selectableRowIds: readonly ArRowId[];
  selectedRows: Readonly<Record<ArRowId, unknown>>;
  scope: 'visible' | 'all';
}): ArTableSelectionScope {
  const visibleRowIds = params.selectableRowIds.slice(0, AR_TABLE_PAGE_SIZE);
  const selectedVisibleRowIds = visibleRowIds.filter(
    (rowId) => params.selectedRows[rowId] === true,
  );
  const everyVisibleTicked =
    visibleRowIds.length > 0 && selectedVisibleRowIds.length === visibleRowIds.length;

  return {
    visibleRowIds,
    selectedVisibleRowIds,
    everyVisibleTicked,
    isAllRows: params.scope === 'all' && everyVisibleTicked,
  };
}

export { resolveSelectionScope };
export type { ArTableSelectionScope };
